import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, MotionConfig } from "framer-motion";
import { SPRING, useMotion } from "./motion";
import { RotateCcw, Volume2, VolumeX, UtensilsCrossed, LayoutDashboard, Crown, Loader2, Columns2, Calculator, X, HandPlatter, Users, Play, Pause, Clapperboard } from "lucide-react";
import Logo from "./components/Logo";
import ClienteView from "./components/ClienteView";
import GerenciaView from "./components/GerenciaView";
import DuenoView from "./components/DuenoView";
import MozoView from "./components/MozoView";
import { get, post, patch, getTableFromUrl, money } from "./api";
import { playNewOrder, playCashAlert } from "./sounds";

const VIEWS = [
  { id: "cliente", label: "cliente", icon: <UtensilsCrossed size={14} /> },
  { id: "mozo", label: "mozo", icon: <HandPlatter size={14} /> },
  { id: "gerencia", label: "gerencia", icon: <LayoutDashboard size={14} /> },
  { id: "dueno", label: "dueño", icon: <Crown size={14} /> },
];

// fuera de CalcModal: si se define adentro, cada cambio remonta el slider y se corta el arrastre
function Row({ label, val, set, min, max, step, unit, testid }) {
  return (
    <div className="mb-4">
      <div className="flex justify-between text-xs font-bold mb-1.5">
        <span className="lowercase">{label}</span>
        <span className="font-mono2" data-testid={testid}>{unit === "$" ? money(val) : `${val}${unit}`}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={val} onChange={(e) => set(Number(e.target.value))}
        className="w-full accent-[#0A0A0A]" data-testid={`${testid}-input`} />
    </div>
  );
}

function CalcModal({ open, onClose, onGoCliente }) {
  const [mesas, setMesas] = useState(12);
  const [ticket, setTicket] = useState(38000);
  const [dias, setDias] = useState(6);
  const [propina, setPropina] = useState(7);
  const [ticketLift, setTicketLift] = useState(8);
  const [minSaved, setMinSaved] = useState(8);
  const [tipLift, setTipLift] = useState(3);

  // cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  // facturación mensual estimada: mesas × 2 rotaciones × días × 4.3 semanas × ticket
  const facturacion = Math.round(mesas * 2 * dias * 4.3 * ticket);
  const perdidos = Math.round(facturacion * (ticketLift / 100));
  const rotacion = Math.round(facturacion * (minSaved / 90));
  const propinas = Math.round(facturacion * (tipLift / 100));
  // nunca más del 20% de la facturación
  const total = Math.min(perdidos + rotacion + propinas, Math.round(facturacion * 0.20));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="calc-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-2xl sm:rounded-lg p-6 w-full sm:w-[480px] max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <p className="font-extrabold text-lg lowercase">¿cuánto sumás con QR?</p>
          <button data-testid="calc-close" onClick={onClose}><X size={18} /></button>
        <button data-testid="calc-go-cliente-top" onClick={() => { onGoCliente(); onClose(); }}
          className="text-[11px] font-bold bg-[#A0CDE8] text-[#0A0A0A] px-2.5 py-1.5 rounded-lg">
          ver cómo funciona →
        </button>
        </div>

        <Row label="cantidad de mesas" val={mesas} set={setMesas} min={4} max={40} step={1} unit="" testid="calc-mesas" />
        <Row label="ticket promedio" val={ticket} set={setTicket} min={15000} max={90000} step={1000} unit="$" testid="calc-ticket" />
        <Row label="días abiertos por semana" val={dias} set={setDias} min={3} max={7} step={1} unit="" testid="calc-dias" />
        <Row label="propina actual" val={propina} set={setPropina} min={0} max={15} step={1} unit="%" testid="calc-propina" />

        <div className="bg-[#E3F0F9] border border-[#A0CDE8] rounded-lg p-4 my-4 text-center">
          <p className="text-[11px] font-bold text-[#525252] lowercase">facturación mensual estimada</p>
          <p className="font-mono2 font-extrabold text-2xl" data-testid="calc-facturacion">{money(facturacion)}</p>
        </div>

        <div className="bg-[#0A0A0A] text-white rounded-lg p-5 my-5">
          <p className="text-[11px] font-bold text-white/50 lowercase">resultado estimado</p>
          <p className="font-mono2 font-extrabold text-4xl mt-1 text-[#A0CDE8]" data-testid="calc-total">{money(total)}</p>
          <p className="text-xs text-white/50">más por mes · sobre una facturación estimada de {money(facturacion)}</p>
          <div className="mt-4 space-y-1.5 text-xs font-mono2">
            <div className="flex justify-between"><span className="text-white/60">pedidos que hoy se pierden</span><span data-testid="calc-perdidos">{money(perdidos)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">mesas que rotan más rápido</span><span data-testid="calc-rotacion">{money(rotacion)}</span></div>
            <div className="flex justify-between"><span className="text-white/60">propinas</span><span data-testid="calc-propinas">{money(propinas)}</span></div>
          </div>
        </div>

        <p className="text-xs font-bold lowercase mb-2">supuestos (editables)</p>
        <Row label="ticket extra por pedido QR" val={ticketLift} set={setTicketLift} min={2} max={20} step={1} unit="%" testid="calc-lift" />
        <Row label="minutos menos por mesa" val={minSaved} set={setMinSaved} min={2} max={20} step={1} unit=" min" testid="calc-min" />
        <Row label="propina extra" val={tipLift} set={setTipLift} min={0} max={8} step={1} unit="%" testid="calc-tiplift" />

        <p className="text-[11px] text-neutral-400 mb-4">estimación: cada local es distinto</p>
        <button data-testid="calc-go-cliente" onClick={() => { onClose(); onGoCliente(); }}
          className="w-full bg-[#A0CDE8] text-[#0A0A0A] font-bold py-3.5 rounded-lg hover:brightness-95 active:scale-[0.98] transition">
          ver cómo funciona →
        </button>
      </motion.div>
    </div>
  );
}

const MARQUEE = "escaneás el QR · pedís sin esperar · la cocina lo ve al instante · pagás desde la mesa · las malas reseñas las resolvés vos · bodegón demo · ";

export default function App() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const tableFromUrl = getTableFromUrl();
  const hasMesaParam = new URLSearchParams(window.location.search).has("mesa");
  const [view, setView] = useState(hasMesaParam ? "cliente" : "cliente");
  const [splitView, setSplitView] = useState(false);
  const [state, setState] = useState(null);
  const [muted, setMuted] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [gerenciaSection, setGerenciaSection] = useState("pedidos");
  const [duenoSection, setDuenoSection] = useState("impacto");
  const [mozoId, setMozoId] = useState("m1");
  const [calcOpen, setCalcOpen] = useState(false);
  const [secondPhone, setSecondPhone] = useState(false);
  const [pres, setPres] = useState({ active: false, step: 0, paused: false });
  const [presScreen, setPresScreen] = useState(null);
  const presOrderId = useRef(null);
  const presActionDone = useRef(new Set());
  const ordersSigRef = useRef("");
  const alertsSigRef = useRef("");

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const refresh = async () => {
    try {
      const s = await get("/state");
      setState(s);
      return s;
    } catch (e) {
      return null;
    }
  };

  useEffect(() => {
    refresh();
    const iv = setInterval(async () => {
      const s = await refresh();
      if (!s) return;
      const open = s.orders.filter((o) => !o.paid);
      const sig = open.map((o) => o.id + o.status).join("|");
      if (ordersSigRef.current && sig !== ordersSigRef.current) {
        const newOnes = open.filter((o) => o.status === "nuevo" && !ordersSigRef.current.includes(o.id));
        if (newOnes.length) playNewOrder(!muted);
      }
      ordersSigRef.current = sig;
      const cashAlerts = s.alerts.filter((a) => a.type === "efectivo" && a.status === "pendiente");
      const aSig = cashAlerts.map((a) => a.id).join("|");
      if (alertsSigRef.current && aSig !== alertsSigRef.current && cashAlerts.length) {
        playCashAlert(!muted);
      }
      alertsSigRef.current = aSig;
    }, 2500);
    return () => clearInterval(iv);
  }, [muted]);

  const onClienteEvent = (kind) => {
    if (kind === "nuevo_pedido") playNewOrder(!muted);
    if (kind === "efectivo") playCashAlert(!muted);
  };
  const onEvent = () => {};
  const goGuide = () => { setView("gerencia"); setGerenciaSection("pedidos"); };

  // ---------------- modo presentación ----------------
  const PRES_STEPS = [
    { view: "cliente", screen: "welcome", caption: "así ve el cliente al escanear el QR de su mesa", dur: 3200 },
    { view: "cliente", screen: "menu", caption: "el menú con fotos, precios y etiquetas; agrega al carrito", dur: 4200 },
    { view: "cliente", screen: "tracking", caption: "confirmamos un pedido de la mesa 7…", dur: 3000,
      action: async () => {
        const r = await post("/orders", { table: 7, items: [{ dish_id: "d01", qty: 1, note: "sin cebolla" }, { dish_id: "d15", qty: 2, note: "" }], by_name: "presentación" });
        presOrderId.current = r.order.id;
      } },
    { view: "gerencia", gsection: "pedidos", caption: "entra solo a la gerencia: suena y aparece en \"nuevos\"", dur: 4200,
      action: async () => { if (presOrderId.current) await patch(`/orders/${presOrderId.current}/status`, { status: "en_cocina" }); } },
    { view: "gerencia", gsection: "cocina", caption: "la cocina lo ve como ticket grande y lo marca listo", dur: 4200,
      action: async () => { if (presOrderId.current) await patch(`/orders/${presOrderId.current}/status`, { status: "listo" }); } },
    { view: "gerencia", gsection: "pedidos", caption: "pasa a \"listos para servir\" y se entrega a la mesa", dur: 4200,
      action: async () => { if (presOrderId.current) await patch(`/orders/${presOrderId.current}/status`, { status: "entregado" }); } },
    { view: "cliente", screen: "tracking", caption: "el cliente sigue todo en vivo desde su celular", dur: 3200 },
    { view: "cliente", screen: "bill", caption: "pide la cuenta, deja propina y elige cómo pagar", dur: 4200 },
    { view: "cliente", screen: "cash", caption: "elige efectivo…", dur: 2000,
      action: async () => { await post("/payments", { table: 7, method: "efectivo", tip: 4000 }); } },
    { view: "gerencia", gsection: "pedidos", caption: "salta la alerta roja y la caja confirma el cobro", dur: 4200,
      action: async () => { await post("/payments/confirm-cash", { table: 7, method: "efectivo" }); } },
    { view: "cliente", screen: "receipt", caption: "el cliente recibe el comprobante al instante", dur: 3600 },
    { view: "cliente", screen: "review", caption: "deja su reseña: 4-5★ van a Google, 1-3★ quedan adentro", dur: 3600 },
    { view: "gerencia", gsection: "caja", caption: "todo impacta en la caja del turno", dur: 3800 },
    { view: "dueno", dsection: "impacto", caption: "y el dueño mide el impacto del QR en vivo", dur: 4400 },
    { view: "dueno", dsection: "resumen", caption: "ventas, gráficos, sucursales y reputación en un panel", dur: 4400 },
  ];

  const startPresentation = async () => {
    await post("/reset", {});
    await refresh();
    setResetSignal((n) => n + 1);
    presOrderId.current = null;
    presActionDone.current = new Set();
    setPres({ active: true, step: 0, paused: false });
  };
  const stopPresentation = () => setPres({ active: false, step: 0, paused: false });
  const togglePause = () => setPres((p) => ({ ...p, paused: !p.paused }));

  useEffect(() => {
    if (!pres.active || pres.paused) return;
    const step = PRES_STEPS[pres.step];
    if (!step) { stopPresentation(); return; }
    setView(step.view);
    if (step.gsection) setGerenciaSection(step.gsection);
    if (step.dsection) setDuenoSection(step.dsection);
    setPresScreen(step.screen || null);
    if (step.action && !presActionDone.current.has(pres.step)) {
      presActionDone.current.add(pres.step);
      step.action().catch(() => {});
    }
    const t = setTimeout(() => {
      setPres((p) => (p.step + 1 >= PRES_STEPS.length ? { active: false, step: 0, paused: false } : { ...p, step: p.step + 1 }));
    }, step.dur);
    return () => clearTimeout(t);
  }, [pres.active, pres.step, pres.paused]);

  const resetDemo = async () => {
    setResetting(true);
    await post("/reset", {});
    await refresh();
    ordersSigRef.current = "";
    alertsSigRef.current = "";
    setResetSignal((n) => n + 1);
    setResetting(false);
  };

  const splitCliente = view === "cliente" && splitView && !isMobile;

  // crossfade de vistas 200ms; con reduced-motion, fade de 100ms sin desplazamiento
  const mo = useMotion();
  const viewAnim = mo.reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.1 } }
    : { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 }, transition: { duration: 0.2 } };

  return (
    <MotionConfig reducedMotion="user">
    <div className="h-screen w-screen bg-[#FAFAF7] flex flex-col overflow-hidden">
      <header className="shrink-0 bg-[#FAFAF7] border-b-2 border-[#0A0A0A] z-40">
        <div className="flex items-center gap-2 sm:gap-4 px-3 sm:px-5 h-14">
          <div className="flex items-center gap-2">
            <Logo size={28} />
            <div className="leading-none hidden sm:block">
              <p className="font-extrabold lowercase text-sm">bodegón demo</p>
              <p className="text-[10px] text-neutral-500">pedidos por QR · demo interactiva</p>
            </div>
          </div>

          <div className="mx-auto flex items-center bg-white border-2 border-[#0A0A0A] rounded-lg p-0.5 sm:p-1 gap-0.5 sm:gap-1" data-testid="view-switcher">
            {VIEWS.map((v) => (
              <button key={v.id} data-testid={`switch-${v.id}`} onClick={() => setView(v.id)}
                className={`relative flex items-center gap-1 sm:gap-1.5 px-2 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-bold transition ${view === v.id ? "text-[#0A0A0A]" : "text-neutral-500 hover:text-[#0A0A0A]"}`}>
                {view === v.id && (
                  <motion.span layoutId="view-pill" className="absolute inset-0 bg-[#A0CDE8] rounded-md" transition={SPRING} />
                )}
                <span className="relative flex items-center gap-1 sm:gap-1.5">{v.icon}<span className={isMobile ? "sr-only" : ""}>{v.label}</span></span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            <button data-testid="calc-open" onClick={() => setCalcOpen(true)}
              className="flex items-center gap-1.5 text-xs font-bold bg-[#A0CDE8] text-[#0A0A0A] rounded-lg px-3 sm:px-4 py-2.5 hover:brightness-95 transition min-h-[44px]">
              <Calculator size={14} /> <span className={isMobile ? "sr-only" : ""}>¿cuánto sumás?</span>
            </button>
            {!isMobile && view === "gerencia" && (
              <button data-testid="top-sound-toggle" onClick={() => setMuted(!muted)} title={muted ? "activar sonido" : "silenciar"}
                className="flex items-center justify-center w-11 h-11 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0A0A0A] transition">
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            )}
            {!isMobile && view === "cliente" && (
              <>
                <button data-testid="second-phone-toggle" onClick={() => setSecondPhone(!secondPhone)} title="simular otra persona en la mesa"
                  className={`flex items-center justify-center w-11 h-11 rounded-lg border transition ${secondPhone ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E7EB] hover:border-[#0A0A0A]"}`}>
                  <Users size={16} />
                </button>
                <button data-testid="split-view-toggle" onClick={() => setSplitView(!splitView)} title="vista dividida"
                  className={`flex items-center justify-center w-11 h-11 rounded-lg border transition ${splitView ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#E5E7EB] hover:border-[#0A0A0A]"}`}>
                  <Columns2 size={16} />
                </button>
              </>
            )}
            {!pres.active ? (
              <button data-testid="presentation-start" onClick={startPresentation} title="modo presentación"
                className="flex items-center gap-1.5 text-xs font-bold bg-[#0A0A0A] text-white rounded-lg px-3 sm:px-4 hover:bg-neutral-800 transition min-h-[44px]">
                <Play size={14} /> <span className={isMobile ? "sr-only" : ""}>presentación</span>
              </button>
            ) : (
              <>
                <button data-testid="presentation-pause" onClick={togglePause} title={pres.paused ? "reanudar" : "pausar"}
                  className="flex items-center justify-center w-11 h-11 rounded-lg bg-[#A0CDE8] text-[#0A0A0A] transition">
                  {pres.paused ? <Play size={16} /> : <Pause size={16} />}
                </button>
                <button data-testid="presentation-stop" onClick={stopPresentation} title="salir de la presentación"
                  className="flex items-center justify-center w-11 h-11 rounded-lg border-2 border-[#DC2626] bg-white text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition">
                  <X size={16} />
                </button>
              </>
            )}
            <button data-testid="reset-demo-btn" onClick={resetDemo} disabled={resetting} title="reiniciar demo"
              className="flex items-center justify-center w-11 h-11 rounded-lg border border-[#E5E7EB] bg-white hover:border-[#0A0A0A] transition disabled:opacity-50">
              {resetting ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />}
            </button>
          </div>
        </div>
        {!isMobile && view === "cliente" && (
          <div className="border-t border-[#E5E7EB] bg-[#A0CDE8] text-[#0A0A0A] overflow-hidden h-7 flex items-center" data-testid="marquee">
            <div className="marquee-track text-[11px] font-bold font-mono2 lowercase tracking-wide">
              <span className="pr-2">{MARQUEE.repeat(3)}</span>
              <span className="pr-2">{MARQUEE.repeat(3)}</span>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 min-h-0">
        {!state ? (
          <div className="h-full p-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="loading-skeleton">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-[#E9E9E4] rounded-lg h-40 animate-pulse" style={{ animationDelay: `${i * 120}ms` }} />
            ))}
          </div>
        ) : (
          // ClienteView queda siempre montado en el mismo lugar (solo se oculta) para no perder su pantalla al cambiar de vista.
          // Sin vista dividida, todas las vistas comparten la misma celda: el crossfade no apila alturas.
          <div className={`h-full ${splitCliente ? (secondPhone ? "grid grid-cols-[430px_430px_1fr]" : "grid grid-cols-[430px_1fr]") : "grid grid-cols-1 grid-rows-1 [&>*]:col-start-1 [&>*]:row-start-1 [&>*]:min-h-0 [&>*]:min-w-0"}`}>
            <div className={view === "cliente" ? (splitCliente ? "border-r-2 border-[#0A0A0A] py-4 overflow-hidden" : `h-full ${isMobile ? "" : "py-6 overflow-hidden"}`) : "hidden"}
              data-testid={splitCliente ? "split-cliente" : undefined}>
              <ClienteView state={state} setState={setState} refresh={refresh} onClienteEvent={onClienteEvent} resetSignal={resetSignal} onGuide={goGuide} isMobile={isMobile} openCalc={() => setCalcOpen(true)} forceScreen={pres.active ? presScreen : null} />
            </div>
            {splitCliente && secondPhone && (
              <div className="border-r-2 border-[#0A0A0A] py-4 overflow-hidden" data-testid="split-cliente-2">
                <ClienteView state={state} setState={setState} refresh={refresh} onClienteEvent={onClienteEvent} resetSignal={resetSignal} onGuide={goGuide} isMobile={false} openCalc={() => setCalcOpen(true)} forceScreen={pres.active ? presScreen : null} />
              </div>
            )}
            {splitCliente && (
              <div data-testid="split-gerencia">
                <GerenciaView state={state} setState={setState} refresh={refresh} muted={muted} setMuted={setMuted} onEvent={onEvent} isMobile={false} section={gerenciaSection} setSection={setGerenciaSection} />
              </div>
            )}
            <AnimatePresence initial={false}>
              {view === "mozo" && (
                <motion.div key="mozo" {...viewAnim} className="h-full">
                  <MozoView state={state} setState={setState} isMobile={isMobile} mozoId={mozoId} setMozoId={setMozoId} />
                </motion.div>
              )}
              {view === "gerencia" && !splitCliente && (
                <motion.div key="gerencia" {...viewAnim} className="h-full">
                  <GerenciaView state={state} setState={setState} refresh={refresh} muted={muted} setMuted={setMuted} onEvent={onEvent} isMobile={isMobile} section={gerenciaSection} setSection={setGerenciaSection} />
                </motion.div>
              )}
              {view === "dueno" && (
                <motion.div key="dueno" {...viewAnim} className="h-full">
                  <DuenoView state={state} setState={setState} refresh={refresh} isMobile={isMobile} section={duenoSection} setSection={setDuenoSection} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}
      </main>
      <CalcModal open={calcOpen} onClose={() => setCalcOpen(false)} onGoCliente={() => setView("cliente")} />
      {pres.active && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-[92vw]" data-testid="presentation-caption">
          <div className="flex items-center gap-3 bg-[#0A0A0A] text-white rounded-lg px-5 py-3 shadow-2xl border-2 border-[#A0CDE8]">
            <span className="flex items-center gap-1.5 font-mono2 text-[11px] font-bold text-[#A0CDE8] shrink-0">
              <Clapperboard size={13} /> {String(pres.step + 1).padStart(2, "0")}/{PRES_STEPS.length}
            </span>
            <p className="text-sm font-semibold lowercase">{PRES_STEPS[pres.step]?.caption}</p>
            {pres.paused && <span className="text-[10px] font-bold bg-[#FEF9C3] text-[#a16207] px-2 py-0.5 rounded-md shrink-0">pausado</span>}
          </div>
        </div>
      )}
    </div>
    </MotionConfig>
  );
}
